// src/Dashboard.js

import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Grid, Box, ToggleButton, ToggleButtonGroup, Skeleton } from '@mui/material';
import { Title, fetchUtils, useNotify } from 'react-admin';
import { 
    LineChart, 
    Line, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    Legend, 
    ResponsiveContainer,
    PieChart, 
    Pie, 
    Cell,
} from 'recharts';
import PeopleIcon from '@mui/icons-material/People';
import CarIcon from '@mui/icons-material/DirectionsCar';
import PendingIcon from '@mui/icons-material/PendingActions';
import PremiumIcon from '@mui/icons-material/Star';
import PassengerIcon from '@mui/icons-material/Hail'; // Icon for Passengers
import DriverIcon from '@mui/icons-material/AirlineSeatReclineNormal'; // Icon for Drivers
import { API_URL } from './config'; // استيراد الرابط المركزي

const StatCard = ({ icon, title, value, loading }) => (
    <Card sx={{ display: 'flex', alignItems: 'center', padding: '16px', height: '100%' }}>
        {icon}
        <Box marginLeft="16px">
            <Typography color="textSecondary" gutterBottom>{title}</Typography>
            {loading ? (
                <Skeleton variant="text" width={80} height={32} />
            ) : (
                <Typography variant="h5" component="h2">{value != null ? value : '-'}</Typography> // Show '-' if value is null/undefined
            )}
        </Box>
    </Card>
);

export const Dashboard = () => {
    const [stats, setStats] = useState({});
    const [userChartData, setUserChartData] = useState([]);
    const [rideChartData, setRideChartData] = useState([]);
    const [pieChartData, setPieChartData] = useState({ userTypes: [], rideStatuses: [], rideTypes: [] });
    const [timeframe, setTimeframe] = useState('weekly');
    const [loadingStats, setLoadingStats] = useState(true);
    const [loadingCharts, setLoadingCharts] = useState(true);
    const notify = useNotify();

    // Fetch Stats
    useEffect(() => {
        setLoadingStats(true);
        const token = localStorage.getItem('token');
        const options = {
            headers: new Headers({ 
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
        };
        // **ملاحظة**: السيرفر يتوقع `id` في المسار، نستخدم "dashboard-stats" كـ id وهمي
        fetchUtils.fetchJson(`${API_URL}/admin/dashboard-stats/dashboard-stats`, options)
            .then(({ json }) => {
                setStats(json);
                setLoadingStats(false);
            })
            .catch(error => {
                console.error("Dashboard Stats fetch error:", error);
                notify('Could not load dashboard stats.', { type: 'warning' });
                setLoadingStats(false);
            });
    }, [notify]);

    // Fetch Chart Data based on timeframe
    useEffect(() => {
        setLoadingCharts(true);
        const token = localStorage.getItem('token');
        const options = {
            headers: new Headers({ 
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
        };

        Promise.all([
            fetchUtils.fetchJson(`${API_URL}/admin/charts/user-registrations?timeframe=${timeframe}`, options),
            fetchUtils.fetchJson(`${API_URL}/admin/charts/rides-over-time?timeframe=${timeframe}`, options),
            fetchUtils.fetchJson(`${API_URL}/admin/charts/pie-stats`, options) // Pie charts don't depend on timeframe
        ]).then(([userChartRes, rideChartRes, pieChartRes]) => {
            setUserChartData(userChartRes.json);
            setRideChartData(rideChartRes.json);
            setPieChartData(pieChartRes.json);
            setLoadingCharts(false);
        }).catch(error => {
            console.error("Dashboard Charts fetch error:", error);
            notify('Could not load dashboard charts.', { type: 'warning' });
            setLoadingCharts(false);
        });

    }, [timeframe, notify]);

    const handleTimeframeChange = (event, newTimeframe) => {
        if (newTimeframe !== null) {
            setTimeframe(newTimeframe);
        }
    };

    // Pie Chart Helpers
    const PIE_COLORS_USERS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042']; // Added more colors
    const PIE_COLORS_RIDES = ['#82ca9d', '#FF8042', '#FF0000', '#8884d8', '#cccccc']; // Added grey for other statuses
    const PIE_COLORS_RIDE_TYPES = ['#0088FE', '#FF8042', '#00C49F']; // ألوان جديدة للرسم البياني الثالث

    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        if (percent < 0.05) return null; // Hide small labels

        return (
            <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="12px" fontWeight="bold">
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    return (
        <>
            <Title title="Dashboard" />
            {/* Stat Cards */}
            <Grid container spacing={3} sx={{ marginTop: '1em', marginBottom: '1em' }}>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <StatCard icon={<PeopleIcon color="primary" sx={{ fontSize: 36 }}/>} title="Total Users" value={stats.totalUsers} loading={loadingStats} />
                </Grid>
                 <Grid item xs={12} sm={6} md={3} lg={2}>
                    <StatCard icon={<DriverIcon color="action" sx={{ fontSize: 36 }}/>} title="Drivers" value={stats.totalDrivers} loading={loadingStats} />
                </Grid>
                 <Grid item xs={12} sm={6} md={3} lg={2}>
                    <StatCard icon={<PassengerIcon color="success" sx={{ fontSize: 36 }}/>} title="Passengers" value={stats.totalPassengers} loading={loadingStats} />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <StatCard icon={<CarIcon color="secondary" sx={{ fontSize: 36 }}/>} title="Total Rides" value={stats.totalRides} loading={loadingStats} />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <StatCard icon={<PendingIcon color="error" sx={{ fontSize: 36 }}/>} title="Pending Verifications" value={stats.pendingVerifications} loading={loadingStats} />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                    <StatCard icon={<PremiumIcon sx={{ fontSize: 36, color: '#FFC107' }}/>} title="Premium Users" value={stats.premiumUsers} loading={loadingStats} />
                </Grid>
            </Grid>

            {/* Charts Section */}
            <Grid container spacing={3} sx={{ marginTop: '1em' }}>
                
                {/* Line Charts */}
                <Grid item xs={12} lg={8}>
                     {/* User Registrations Chart */}
                    <Card sx={{ mb: 3 }}> {/* Add margin bottom */}
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6">User Registration</Typography>
                                <ToggleButtonGroup value={timeframe} exclusive onChange={handleTimeframeChange} size="small">
                                    <ToggleButton value="daily">Daily</ToggleButton>
                                    <ToggleButton value="weekly">Weekly</ToggleButton>
                                    <ToggleButton value="monthly">Monthly</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            <Box sx={{ width: '100%', height: 250 }}>
                                {loadingCharts ? <Skeleton variant="rectangular" width="100%" height={250} /> : (
                                    <ResponsiveContainer>
                                        <LineChart data={userChartData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" fontSize={10} />
                                            <YAxis fontSize={10}/>
                                            <Tooltip />
                                            <Legend wrapperStyle={{ fontSize: "12px" }}/>
                                            <Line type="monotone" dataKey="newUsers" stroke="#8884d8" activeDot={{ r: 6 }} strokeWidth={2}/>
                                        </LineChart>
                                    </ResponsiveContainer>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Rides Over Time Chart */}
                    <Card>
                        <CardContent>
                            <Typography variant="h6" mb={2}>Rides Over Time</Typography>
                             <Box sx={{ width: '100%', height: 250 }}>
                                {loadingCharts ? <Skeleton variant="rectangular" width="100%" height={250} /> : (
                                    <ResponsiveContainer>
                                        <LineChart data={rideChartData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" fontSize={10} />
                                            <YAxis fontSize={10} />
                                            <Tooltip />
                                            <Legend wrapperStyle={{ fontSize: "12px" }} />
                                            <Line type="monotone" dataKey="newRides" stroke="#82ca9d" activeDot={{ r: 6 }} strokeWidth={2} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Pie Charts */}
                <Grid item xs={12} lg={4}>
                    {/* User Types Pie Chart (NEW) */}
                    <Card sx={{ mb: 3 }}> {/* Add margin bottom */}
                        <CardContent>
                            <Typography variant="h6" textAlign="center">Ride Types</Typography>
                            <Box sx={{ width: '100%', height: 250 }}>
                                {loadingCharts ? <Skeleton variant="circular" width={150} height={150} sx={{ margin: 'auto', mt: 1 }} /> : (
                                    <ResponsiveContainer>
                                        <PieChart>
                                            <Pie
                                                data={pieChartData.rideTypes}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={80}
                                                fill="#8884d8"
                                                labelLine={false}
                                                label={renderCustomizedLabel}
                                            >
                                                {/* Use different colors for this chart */}
                                                {pieChartData.rideTypes && pieChartData.rideTypes.map((entry, index) => (
                                                    <Cell key={`cell-ride-type-${index}`} fill={PIE_COLORS_RIDE_TYPES[index % PIE_COLORS_RIDE_TYPES.length]} /> 
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{ fontSize: "12px" }}/>
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Ride Statuses Pie Chart */}
                     <Card>
                        <CardContent>
                            <Typography variant="h6" textAlign="center">Ride Statuses</Typography>
                             <Box sx={{ width: '100%', height: 250 }}>
                                {loadingCharts ? <Skeleton variant="circular" width={150} height={150} sx={{ margin: 'auto', mt: 1 }} /> : (
                                    <ResponsiveContainer>
                                        <PieChart>
                                            <Pie
                                                data={pieChartData.rideStatuses}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={80}
                                                fill="#8884d8"
                                                labelLine={false}
                                                label={renderCustomizedLabel}
                                            >
                                                {pieChartData.rideStatuses && pieChartData.rideStatuses.map((entry, index) => (
                                                    <Cell key={`cell-ride-${index}`} fill={PIE_COLORS_RIDES[index % PIE_COLORS_RIDES.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{ fontSize: "12px" }}/>
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </>
    );
};